import React, { useEffect, useMemo, useRef } from 'react'
import './jobs-list.scss'
import classnames from 'classnames'
import clone from 'clone-deep'
import * as deepEqual from 'react-fast-compare'
import { useMountedRef } from '../../hooks/use-mounted-ref'
import { fmtDateString, fmtNumberAsDelineatedCount } from '../../helpers/formatting'
import { useAxiosCancelToken } from '../../hooks/use-axios-cancel-token'
import EntityDisplay, { EntityTitle } from '../../components/data-table-view/entity-display'
import { useService } from '@halo/hooks/use-service'
import { AppState } from '@halo/stores/app.state'
import { fetchJobFilters, fetchJobs } from '@halo/api/jobs.api'
import {
    CloseCircleOutlined,
    CopyOutlined,
    DeploymentUnitOutlined,
    Loading3QuartersOutlined,
    ReloadOutlined,
} from '@ant-design/icons'
import { Badge, Flex, Input, Table, Tooltip } from 'antd'
import { Button, FilterBar } from '@pushly/aqe/lib/components'
import type {
    ActiveFilter,
    ButtonAction,
    FilterDefinition,
    FilterSelectOption,
} from '@pushly/aqe/lib/components'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import moment from 'moment-timezone'
import { useLocation } from 'react-router'
import queryString from 'query-string'
import { stripUndefined } from '@halo/helpers/clean-objects'
import { collateConfig, buildApiOrderConfig } from '@halo/components/data-table-view/helpers'
import { useRefState } from '@halo/hooks/use-ref-state'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import ExpandedJobView from '@halo/components/jobs-list/expanded-job-view'
import CopyToClipboard from 'react-copy-to-clipboard'
import { DataTableContext } from '@halo/components/data-table-view/context'
import { DataSourceResolver } from '@halo/components/data-table-view/types'
import { handleJobMarkAsFailed, handleJobClone } from '@halo/components/jobs-list/helpers'
import { observer } from 'mobx-react'
import { JOB_ACTION } from '@halo/components/jobs-list/enums'
import { TablePaginationConfig } from 'antd/es/table/interface'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'

const JobDateTime = observer(({ date }: { date: string }) => {
    const appState = useService<AppState>(AppState)
    const userPreferences = useUserPreferences(appState.currentUser?.id)

    return !date ? (
        <>--</>
    ) : (
        <EntityDisplay
            top={{ left: <span className="job-date">{fmtDateString(date, 'll')}</span> }}
            mid={{
                left: (
                    <span className="job-time">
                        {fmtDateString(date, 'LT z', userPreferences.get(['display_time_zone']) ?? 'utc')}
                    </span>
                ),
            }}
        />
    )
})

const handleCopied = () => {
    simpleNotification('success', 'Copied', undefined, undefined, true)
}

const renderTotal = (total: number, config: any): React.ReactNode => {
    const {
        pagination: { current, pageSize },
    } = clone(config)
    const start = fmtNumberAsDelineatedCount((current - 1) * pageSize + 1)
    const end = fmtNumberAsDelineatedCount(current * pageSize)
    const ttl = fmtNumberAsDelineatedCount(total)

    return total <= pageSize
        ? `Showing ${ttl} of ${ttl} Total Job(s)`
        : `Showing ${start}-${end} of ${ttl} Total Job(s)`
}

const STATUS_OPTIONS: FilterSelectOption[] = [
    { label: 'Submitted', value: 'submitted' },
    { label: 'Queued', value: 'queued' },
    { label: 'Running', value: 'running' },
    { label: 'Completed', value: 'completed' },
    { label: 'Failed', value: 'failed' },
]

const JobsList = () => {
    const appState = useService<AppState>(AppState)

    const [_mounted, runIfMounted] = useMountedRef()

    /**
     * Handle QueryString variables for
     *
     * @param job_id - defaults search input value
     * @param expand_job_id - expand auto selected id if provided
     */
    const { search: rawQs } = useLocation()
    const qs = queryString.parse(rawQs.replace(/^\?/, ''))
    const qsJobId = (qs.job_id ?? qs.jobId ?? '').toString()
    const qsExpandJobId = (qs.expand_job_id ?? qs.expandJobId ?? false).toString().toLowerCase() === 'true'
    const defaultExpandedRowKeys: string[] = []
    if (qsJobId && qsExpandJobId) {
        defaultExpandedRowKeys.push(qsJobId)
    }

    /**
     * Data View configuration & state manager
     *
     * @param loading
     * @param data - current page data
     * @param pagination - AntD pagination config
     * @param filters - AntD filters config + { tableSearchValue: string }
     * @param sortOrder - AntD sortOrder config
     */
    // @ts-ignore
    const [jobs, setJobs] = useRefState<any>({
        loading: false,
        data: undefined,
        pagination: {
            pageSize: 100,
            current: 1,
            total: 100,

            size: 'small',
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showQuickJumper: true,
            // @ts-ignore
            showTotal: (total: number) => renderTotal(total, jobs.current),
            itemRender: (current: any, type: string, originalElement: any) => {
                if (type === 'prev') {
                    return <a>Previous</a>
                }
                if (type === 'next') {
                    return <a>Next</a>
                }
                if (type === 'page' || /^jump/.test(type)) {
                    return ''
                }

                return originalElement
            },
        } as TablePaginationConfig,
        filters: stripUndefined({
            tableSearchValue: qsJobId,
        }),
        sortOrder: {
            columnKey: 'created_at',
            field: 'created_at',
            order: 'descend',
        },
    })

    const [paginationLinks, setPaginationLinks] = React.useState<null | IApiResponsePaginationLinks>(null)

    const [cancelToken, resetCancelToken] = useAxiosCancelToken()
    const [isTableExpanded, setIsTableExpanded] = useRefState(false)
    const previousJobIds = useRef<string[] | undefined>(undefined)
    const refreshRef = useRef({
        timeout: undefined,
        timestamp: moment(),
    })

    /**
     * DataSourceResolver
     * Pushly extension for AntD table change event
     * accepts undefined values allowing inheritance
     *
     * @param [pagination] - AntD pagination config
     * @param [filters] - AntD filters config + { tableSearchValue: string }
     * @param [sortOrder] - AntD sortOrder config
     * @param [extra] - AntD sortOrder config
     */
    const fetchData: DataSourceResolver<any> = async (pagination, filters, sortOrder, extra): Promise<void> => {
        // clear previous auto-refresh handler
        clearTimeout(refreshRef.current.timeout)

        setJobs({ ...jobs.current, loading: true })
        let data: any[] = []

        // set previously loaded ids
        if (!!extra?.currentDataSource) {
            previousJobIds.current = extra.currentDataSource.map((job: any) => job.id)
        }

        let paginationConfig = clone(collateConfig(pagination, jobs.current.pagination))
        let filtersConfig = clone(collateConfig(filters, jobs.current.filters))
        let sortConfig = clone(collateConfig(sortOrder, jobs.current.sortOrder))

        const pageSizeChanged = paginationConfig.pageSize !== jobs.current.pagination?.pageSize
        const filtersChanged = !deepEqual(filtersConfig, jobs.current.filters)
        const sortChanged = !deepEqual(sortConfig, jobs.current.sortOrder)
        const changesDetected = pageSizeChanged || filtersChanged || sortChanged

        // reset current page on filter/sort/size change
        if (changesDetected) {
            paginationConfig.current = 1
        }

        const searchVal = filtersConfig.tableSearchValue?.trim()
        const orderConfig = buildApiOrderConfig(sortConfig)

        const query = stripUndefined({
            limit: paginationConfig?.pageSize,
            page: paginationConfig?.current,
            search: !searchVal ? undefined : searchVal,
            status: !filtersConfig.status?.length ? undefined : filtersConfig.status.join(','),
            target_name: !filtersConfig.target_name?.length ? undefined : filtersConfig.target_name.join(','),
            order: !orderConfig ? undefined : JSON.stringify(orderConfig),
        })

        resetCancelToken()
        const res = await fetchJobs({
            query,
            showLoadingScreen: false,
            cancelToken: cancelToken.current,
        })

        if (res && res.ok) {
            data = res.data!
            setPaginationLinks(res.meta.links ?? paginationLinks)
        }

        // initial set / reset
        if (
            paginationConfig.current === 1 &&
            (!previousJobIds.current || pageSizeChanged || filtersChanged || sortChanged)
        ) {
            previousJobIds.current = data.map((job) => job.id)
        }

        runIfMounted(() => {
            // set timestamp ref before setJobs
            // to ensure proper value on rerender
            refreshRef.current.timestamp = moment()

            setJobs({
                loading: false,
                data,
                pagination: paginationConfig,
                filters: filtersConfig,
                sortOrder: sortConfig,
            })

            // start timer for next refresh
            processAutoRefresh()
        })
    }

    /**
     * canAutoRefresh determines ability
     * to fetch data after refresh timer
     *
     * disabled auto-refresh if search is
     * populated or any rows are expanded
     */
    const canAutoRefresh = () => {
        const hasSearchValue = !!jobs.current.filters?.tableSearchValue
        return !isTableExpanded.current && !hasSearchValue
    }

    const processAutoRefresh = () => {
        clearTimeout(refreshRef.current.timeout)
        refreshRef.current.timeout = setTimeout(
            () => {
                if (canAutoRefresh()) {
                    fetchData()
                }
            },
            3 * 60 * 1_000,
        )
    }

    const handleRowAction = (action: string | number, job: any) => {
        if (action === JOB_ACTION.FORCE_FAIL) {
            handleJobMarkAsFailed(job, (ok, _j) => {
                if (ok) {
                    fetchData()
                }
            })
        } else if (action === JOB_ACTION.CLONE) {
            handleJobClone(job, (ok, _j) => {
                if (ok) {
                    fetchData()
                }
            })
        }
    }

    /**
     * Initial data load runs once on first mount/render
     */
    useEffect(() => {
        fetchData()
    }, [false])

    /**
     * FilterBar definitions — Target is loaded async from /jobs/filters,
     * Status is a static enum. Both map to comma-separated query params on
     * the jobs endpoint (matching the previous DataTableView wiring).
     */
    const filterDefinitions = useMemo<FilterDefinition[]>(
        () => [
            {
                key: 'target_name',
                label: 'Target',
                type: 'multi-select',
                showOperatorSelect: false,
                loadOptions: async (): Promise<FilterSelectOption[]> => {
                    const res = await fetchJobFilters({ showLoadingScreen: false })
                    if (!res?.ok || !Array.isArray(res.data?.targets)) return []
                    return res.data.targets.map((tgt: any) => ({
                        group: tgt.type,
                        value: tgt.name,
                        label: tgt.name === 'submarine-communications-cable' ? 'scc' : tgt.name,
                    }))
                },
            },
            {
                key: 'status',
                label: 'Status',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: STATUS_OPTIONS,
            },
        ],
        [],
    )

    const getFilterBarValue = (): ActiveFilter[] => {
        const active: ActiveFilter[] = []
        const targets = jobs.current.filters?.target_name ?? []
        const statuses = jobs.current.filters?.status ?? []
        if (targets.length > 0) {
            active.push({ key: 'target_name', operator: 'in', value: targets })
        }
        if (statuses.length > 0) {
            active.push({ key: 'status', operator: 'in', value: statuses })
        }
        return active
    }

    const handleFilterBarApply = (filters: ActiveFilter[]) => {
        const nextTargets = (filters.find((f) => f.key === 'target_name')?.value as string[]) ?? []
        const nextStatuses = (filters.find((f) => f.key === 'status')?.value as string[]) ?? []
        fetchData(undefined, {
            ...jobs.current.filters,
            target_name: nextTargets,
            status: nextStatuses,
        })
    }

    const handleSearch = (value: string) => {
        fetchData(undefined, {
            ...jobs.current.filters,
            tableSearchValue: value,
        })
    }

    const tableHeader = (
        <Flex className="jobs-list-header" gap={8} justify="space-between" align="center">
            <Flex flex={1} gap={8} align="center">
                <Input.Search
                    className="jobs-list-search"
                    style={{ width: 300 }}
                    size="middle"
                    placeholder="Search by ID or Job Name"
                    enterButton={true}
                    defaultValue={qsJobId}
                    onSearch={handleSearch}
                />
                <FilterBar
                    filterDefinitions={filterDefinitions}
                    value={getFilterBarValue()}
                    onApply={handleFilterBarApply}
                    showInlineTags={false}
                    showInlineClearAll={false}
                    size="middle"
                />
            </Flex>
            <span className="last-updated">
                Last Updated @ {refreshRef.current.timestamp.format('LT')}
                {!canAutoRefresh() && <span> (PAUSED) </span>}
                <ReloadOutlined spin={jobs.current.loading} onClick={() => fetchData()} />
            </span>
        </Flex>
    )

    return (
        <div className={classnames('jobs-manager-list')}>
            <DataTableContext.Provider
                value={{
                    loading: jobs.current.loading,
                    currentDataSource: jobs.current.data,
                    resolveDataSource: fetchData,
                }}
            >
                <Table
                    className="aqe-table-well"
                    title={() => tableHeader}
                    size="small"
                    bordered={false}
                    // scroll.y used to freeze header row
                    // scroll.x used to fix bug with col width and scroll.y
                    // https://github.com/ant-design/ant-design/issues/7603#issuecomment-697600114
                    scroll={{ y: 549, x: 'max-content' }}
                    rowKey="id"
                    rowClassName={(dto: any) => {
                        const classNames = [`status-${dto.status}`]

                        if (Array.isArray(previousJobIds.current) && !previousJobIds.current.includes(dto.id)) {
                            classNames.push('new-row')
                        }

                        return classNames.join(' ')
                    }}
                    pagination={false}
                    footer={(currentTableData) => (
                        <SwSimpleLinkPagination
                            currentTableData={currentTableData}
                            links={paginationLinks}
                            onPrev={(_ev, _link, config) => {
                                setJobs({
                                    ...jobs.current,
                                    pagination: {
                                        ...jobs.current.pagination,
                                        current: config.current!,
                                    },
                                })
                                fetchData()
                            }}
                            onNext={(_ev, _link, config) => {
                                setJobs({
                                    ...jobs.current,
                                    pagination: {
                                        ...jobs.current.pagination,
                                        current: config.current!,
                                    },
                                })
                                fetchData()
                            }}
                        />
                    )}
                    locale={{
                        emptyText: <AntdTableEmptyPlaceholder text="No Jobs" icon={<DeploymentUnitOutlined />} />,
                    }}
                    loading={
                        !jobs.current.loading
                            ? false
                            : {
                                  indicator: <Loading3QuartersOutlined spin={true} />,
                              }
                    }
                    dataSource={jobs.current.data}
                    onChange={fetchData}
                    onExpandedRowsChange={(rowKeys) => {
                        const expanded = rowKeys.length > 0
                        setIsTableExpanded(expanded)
                        if (!expanded) {
                            processAutoRefresh()
                        }
                    }}
                    expandedRowRender={(job: any, index: number, indent: number, expanded: boolean) => (
                        <ExpandedJobView job={job} index={index} indent={indent} expanded={expanded} />
                    )}
                    defaultExpandedRowKeys={defaultExpandedRowKeys}
                >
                    <Table.Column
                        title="Name"
                        className="name"
                        dataIndex="name"
                        render={(name, job: any) => {
                            return (
                                <EntityDisplay
                                    top={{
                                        left: <EntityTitle>{name}</EntityTitle>,
                                    }}
                                    mid={{
                                        left: (
                                            <span className="job-id">
                                                <span className="copy">
                                                    <Tooltip title="Copy">
                                                        <CopyToClipboard
                                                            onCopy={handleCopied}
                                                            text={JSON.stringify(job.id)}
                                                        >
                                                            <CopyOutlined />
                                                        </CopyToClipboard>
                                                    </Tooltip>
                                                </span>
                                                {job.id}
                                            </span>
                                        ),
                                    }}
                                />
                            )
                        }}
                    />
                    <Table.Column
                        className="target"
                        title="Target"
                        dataIndex="target_config"
                        render={(config) => {
                            return (
                                <EntityDisplay
                                    top={{
                                        left: (
                                            <EntityTitle>
                                                {config.name === 'submarine-communications-cable'
                                                    ? 'scc'
                                                    : config.name}
                                            </EntityTitle>
                                        ),
                                    }}
                                    mid={{
                                        left: <span className="target-type">{config.type}</span>,
                                    }}
                                />
                            )
                        }}
                    />
                    <Table.Column
                        className="status"
                        title="Status"
                        dataIndex="status"
                        align="center"
                        render={(status) => <Badge dot={true}>{status}</Badge>}
                    />
                    <Table.Column
                        className="started_at"
                        title="Started"
                        dataIndex="started_at"
                        align="center"
                        render={(dt) => <JobDateTime date={dt} />}
                    />
                    <Table.Column
                        className="ended_at"
                        title="Ended"
                        dataIndex="ended_at"
                        align="center"
                        render={(dt) => <JobDateTime date={dt} />}
                    />
                    <Table.Column
                        className="created_at"
                        title="Created"
                        dataIndex="created_at"
                        align="center"
                        render={(dt) => <JobDateTime date={dt} />}
                    />
                    {appState.currentUser?.isInternalDevUser && (
                        <Table.Column
                            className="actions"
                            title="Actions"
                            dataIndex="actions"
                            align="center"
                            render={(_, job: any) => {
                                const status = job.status.toLowerCase()
                                const canMarkAsFailed = status !== 'failed'

                                const rowActions: ButtonAction[] = canMarkAsFailed
                                    ? [
                                          {
                                              key: JOB_ACTION.FORCE_FAIL,
                                              text: 'Mark as Failed',
                                              icon: <CloseCircleOutlined />,
                                              onClick: () => handleRowAction(JOB_ACTION.FORCE_FAIL, job),
                                          },
                                      ]
                                    : []

                                return (
                                    <Button
                                        size="small"
                                        className="job-row-actions-btn"
                                        icon={<CopyOutlined />}
                                        onClick={() => handleRowAction(JOB_ACTION.CLONE, job)}
                                        actions={rowActions.length > 0 ? rowActions : undefined}
                                    >
                                        Clone
                                    </Button>
                                )
                            }}
                        />
                    )}
                </Table>
            </DataTableContext.Provider>
        </div>
    )
}

export default JobsList
